/**
 * The input state machine every device driver (keyboard, touch, gamepad)
 * feeds into. `docs/KNOWN-BUGS.md` item 3: "Without `pointerup`, `pointercancel`
 * and `lostpointercapture` handlers, a tapped direction button repeats
 * forever; press both and the piece vibrates in place and the controls feel
 * reversed... make starting one direction cancel the opposite one."
 *
 * This module is deliberately DOM-free and platform-free: it takes
 * `press(action)` / `release(action)` calls and an `advance(dtMs)` tick, and
 * emits the discrete `InputAction`s the engine understands. That is what
 * makes it a *pure* testable module — a test can drive it with plain
 * function calls and assert exactly what a device driver would have to get
 * right, without a browser, a canvas or a real pointer event in sight.
 * `touch-controls.ts` is the thin DOM layer that turns pointer events into
 * calls here; `keyboard.ts` and `gamepad.ts` are the other two.
 */
import { AutoRepeat } from './repeat.js';

/** The two auto-repeating directions. */
export type Direction = 'left' | 'right';

/** Every action the engine's `InputAction` understands, addressed by this controller. */
export type ControllerAction =
  Direction | 'softDrop' | 'hardDrop' | 'rotateCW' | 'rotateCCW' | 'hold';

export interface InputControllerConfig {
  readonly dasMs?: number;
  readonly arrMs?: number;
}

const DEFAULT_DAS_MS = 140;
const DEFAULT_ARR_MS = 28;

function isDirection(action: ControllerAction): action is Direction {
  return action === 'left' || action === 'right';
}

/**
 * Translates held/pressed state for `left`, `right`, `softDrop`, `hardDrop`,
 * `rotateCW`, `rotateCCW` and `hold` into the discrete `ControllerAction`
 * events the engine's `applyInput` consumes — one per press for single-fire
 * actions, tap-plus-DAS/ARR for the two directions, and one per tick while
 * held for soft drop (hold-to-drop, and the engine's own `tryMove` already
 * guarantees it never force-locks).
 */
export class InputController {
  private readonly left: AutoRepeat;
  private readonly right: AutoRepeat;
  private softDropHeld = false;
  private readonly singleFireHeld = new Set<ControllerAction>();

  constructor(config: InputControllerConfig = {}) {
    const dasMs = config.dasMs ?? DEFAULT_DAS_MS;
    const arrMs = config.arrMs ?? DEFAULT_ARR_MS;
    this.left = new AutoRepeat(dasMs, arrMs);
    this.right = new AutoRepeat(dasMs, arrMs);
  }

  /** Apply new DAS/ARR timings to both directions, live — see `AutoRepeat.configure`. A missing field falls back to the default, exactly as in the constructor. */
  configure(config: InputControllerConfig): void {
    const dasMs = config.dasMs ?? DEFAULT_DAS_MS;
    const arrMs = config.arrMs ?? DEFAULT_ARR_MS;
    this.left.configure(dasMs, arrMs);
    this.right.configure(dasMs, arrMs);
  }

  /** Whether a direction is currently held. Exposed for the touch/gamepad drivers' own bookkeeping. */
  isHeld(action: ControllerAction): boolean {
    if (action === 'left') return this.left.isHeld;
    if (action === 'right') return this.right.isHeld;
    if (action === 'softDrop') return this.softDropHeld;
    return this.singleFireHeld.has(action);
  }

  /**
   * Press an action. Returns the engine actions to dispatch immediately (0 or
   * 1 of them). A repeated press while already held is a no-op — guards
   * against OS keyboard auto-repeat sending more `keydown`s without a
   * `keyup`, and against a duplicate `pointerdown` on the same control.
   */
  press(action: ControllerAction): readonly ControllerAction[] {
    if (this.isHeld(action)) return [];

    if (isDirection(action)) {
      // Starting one direction cancels the opposite — KNOWN-BUGS item 3.
      const opposite: Direction = action === 'left' ? 'right' : 'left';
      if (this.isHeld(opposite)) this.release(opposite);
      const repeater = action === 'left' ? this.left : this.right;
      repeater.press();
      return [action];
    }

    if (action === 'softDrop') {
      this.softDropHeld = true;
      return [action];
    }

    // Single-fire: rotate, hard drop, hold.
    this.singleFireHeld.add(action);
    return [action];
  }

  /**
   * Release an action. Every press must eventually reach this — the DOM
   * layer is responsible for guaranteeing that from `pointerup`,
   * `pointercancel`, `lostpointercapture` and a window-level catch-all, or
   * from `keyup`.
   */
  release(action: ControllerAction): void {
    if (isDirection(action)) {
      (action === 'left' ? this.left : this.right).release();
      return;
    }
    if (action === 'softDrop') {
      this.softDropHeld = false;
      return;
    }
    this.singleFireHeld.delete(action);
  }

  /** Release every action. The window-level catch-all calls this. */
  releaseAll(): void {
    this.left.release();
    this.right.release();
    this.softDropHeld = false;
    this.singleFireHeld.clear();
  }

  /**
   * Advance the repeat clocks by one tick's worth of milliseconds. Returns
   * the engine actions due this tick, in a stable order: left/right repeats,
   * then one `softDrop` per tick while held.
   */
  advance(dtMs: number): readonly ControllerAction[] {
    const events: ControllerAction[] = [];

    const leftFires = this.left.advance(dtMs);
    for (let i = 0; i < leftFires; i++) events.push('left');
    const rightFires = this.right.advance(dtMs);
    for (let i = 0; i < rightFires; i++) events.push('right');

    if (this.softDropHeld) events.push('softDrop');

    return events;
  }
}
